/* Identical file in Marketing & NL-CMS. If you modify this file, make the same changes there,
and vice-versa, unless changes should be specific to this project, then remove this comment. */
import debounce from 'debounce-promise';
import { useField } from 'formik';
import React from 'react';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import { AutocompleteOption, AutocompleteRecord, CreateRecordResponse } from '@utils/interfaces';

const DEBOUNCE_MS = 500;

type Option = AutocompleteOption | AutocompleteRecord;

interface AutocompleteFieldProps {
  className?: string;
  createMethod?: (value: string) => Promise<CreateRecordResponse>;
  fetchMethod?: (value: string) => Promise<Option[]>;
  isClearable?: boolean;
  isCreatable?: boolean;
  isDisabled?: boolean;
  label?: string;
  name: string;
  onChange?: (option: Option) => void;
  options?: Option[];
}

/**
 * AutocompleteField: General purpose autocomplete field for use with Formik
 */
const AutocompleteField = (props: AutocompleteFieldProps) => {
  const { label, name, options, fetchMethod, createMethod, isCreatable = true, className } = props;
  const [field, meta, helpers] = useField(name);
  const SelectFieldComponent = isCreatable ? AsyncCreatableSelect : AsyncSelect;

  const asyncLoadOptions = async (value: string) => {
    let loadedOptions = [];
    if (value.length === 0) {
      loadedOptions = options || [];
    } else {
      // if fetchMethod is present, use it, otherwise, just look at the options list
      loadedOptions = fetchMethod
        ? await fetchMethod(value)
        : options.filter((o) => o.label.toLowerCase().includes(value.toLowerCase()));
    }
    return loadedOptions.map((option) => ({
      label: option?.label || option,
      value: option?.value || option,
    }));
  };

  const debounceLoadOptions = debounce((value: string) => asyncLoadOptions(value), DEBOUNCE_MS, {
    leading: true,
  });

  const handleChange = async (option) => {
    //If a new option is being created, onChange sends back __isNew__ property
    const { label = '', value = '', __isNew__: isNewlyCreated = false } = { ...option };
    let selectedOption = { label, value };

    if (option && isNewlyCreated && createMethod) {
      const result = await createMethod(value);
      selectedOption = { label, value: result.id };
    }

    helpers.setValue(selectedOption);
  };

  return (
    <div
      className={`AutocompleteField form-group ${className || ''} ${
        meta.touched && meta.error ? 'is-invalid' : ''
      }`}
    >
      {label && (
        <label className="input-label" htmlFor={name}>
          {label}
        </label>
      )}
      <SelectFieldComponent
        key={`select-${name}-value-${field.value?.value}`} // rerenders this component if value is changed
        inputId={name}
        className={meta.touched && meta.error ? 'is-invalid' : ''}
        classNamePrefix="react-select"
        onChange={handleChange}
        onBlur={() => helpers.setTouched(true)}
        cacheOptions
        defaultOptions
        defaultValue={field.value?.label && field.value}
        isClearable
        loadOptions={debounceLoadOptions}
        {...props}
      />
      {meta.touched && meta.error && (
        <div className="invalid-feedback">
          {/* meta.error is of type string, but in this case the error lives in an object of type AutocompleteOption, the following code does a force cast */}
          {(meta.error as unknown as AutocompleteOption).value}
        </div>
      )}
    </div>
  );
};

export default AutocompleteField;
